import React from 'react'
import {
  TableCell,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
} from '@resiliatech/birdsong'
import styled from '@emotion/styled'
import { analytics } from 'Modules/Analytics'
import { team } from 'Modules/Analytics/trackActionInterface/Account/team'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  FunderOrgToUserRelationship,
  NonprofitOrgRole,
  User,
} from 'Generated/graphql-types'
import { nonprofitOrgRoles, funderOrgRoles } from 'Context/Constants/constants'
import GET_NONPROFIT_ORG_CONFIG_QUERY from 'Apollo/Queries/GetNonprofitOrgConfig'
import GET_FUNDER_USERS from 'Apollo/Queries/GetFunderUsers'

type Props = {
  updateUserSettings: (userSettings: any) => void
  user: User
  setShowBirdsongSnackbar: (value: React.SetStateAction<boolean>) => void
  setBirdsongSnackbarMessage: (value: React.SetStateAction<string>) => void
  userRelationship?: NonprofitOrgRole | FunderOrgToUserRelationship
  isFunder?: boolean
}

export const RelationshipCell = ({
  userRelationship,
  updateUserSettings,
  user,
  setShowBirdsongSnackbar,
  setBirdsongSnackbarMessage,
  isFunder,
}: Props) => {
  const roles = isFunder ? funderOrgRoles : nonprofitOrgRoles
  const {
    userConfig: { id: currentUserId, permissions, email },
    nonprofitOrgConfig: { orgId: nonprofitOrgId, platformPlan },
    funderOrgConfig: { orgId: funderOrgId },
  } = useAppState()

  const dispatchEditRelationshipSubmitted = analytics.trackActionCreator(
    team.Edit_Account_Type_Submitted
  )
  const [relationship, setRelationship] = React.useState(userRelationship || '')

  const onError = () => {
    setShowBirdsongSnackbar(true)
    setBirdsongSnackbarMessage(
      'Unable to update relationship, please contact support@resilia.com'
    )
  }

  const handleChange = async (event: SelectChangeEvent<unknown>) => {
    setRelationship(event.target.value as NonprofitOrgRole)
    const userSettingsInfo = {
      firstName: user.firstName,
      lastName: user.lastName,
      nonprofitOrgRole: event.target.value as NonprofitOrgRole,
    }

    await updateUserSettings({
      variables: {
        nonprofitOrgId,
        userIdToUpdate: user.id,
        userSettingsInfo,
      },
      refetchQueries: [
        {
          query: GET_NONPROFIT_ORG_CONFIG_QUERY,
          variables: { nonprofitOrgId },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        setBirdsongSnackbarMessage('Relationship updated')
        setShowBirdsongSnackbar(true)
        dispatchEditRelationshipSubmitted({
          userId: currentUserId,
          email,
          nonprofitOrgId,
          funderOrgId,
          platformPlan,
          chosenAccountType: event.target.value as NonprofitOrgRole,
        })
      },
      onError,
    })
  }

  const handleFunderChange = async (event: SelectChangeEvent<unknown>) => {
    setRelationship(event.target.value as FunderOrgToUserRelationship)
    await updateUserSettings({
      variables: {
        funderId: funderOrgId,
        userIdToUpdate: user.id,
        userSettingsInfo: {
          firstName: user.firstName,
          lastName: user.lastName,
          funderOrgRelationship: event.target
            .value as FunderOrgToUserRelationship,
        },
      },
      refetchQueries: [
        {
          query: GET_FUNDER_USERS,
          variables: { funderOrgId },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        setBirdsongSnackbarMessage('Relationship updated')
        setShowBirdsongSnackbar(true)
        dispatchEditRelationshipSubmitted({
          userId: currentUserId,
          email,
          funderOrgId,
          chosenAccountType: event.target.value as FunderOrgToUserRelationship,
        })
      },
      onError,
    })
  }

  return (
    <TableCell>
      {permissions.includes('team:update') ? (
        <RelationshipSelect
          id='relationship'
          value={relationship}
          onChange={(e) => (isFunder ? handleFunderChange(e) : handleChange(e))}
          size='small'
          aria-label='Relationship Selection'
        >
          {roles.map((role) => (
            <MenuItem key={role.value} value={role.value}>
              {role.label}
            </MenuItem>
          ))}
        </RelationshipSelect>
      ) : (
        // if not admin/owner show chip
        <Chip
          label={
            roles.find((item) => item.value === userRelationship)?.label ||
            'Unknown'
          }
          variant='filled'
          size='small'
        />
      )}
    </TableCell>
  )
}

const RelationshipSelect = styled(Select)`
  width: 232px;
`
